/**
 * Routen und Zugriffsschutz im Frontend.
 *
 * Die Navigation folgt Spec §12: Dashboard · Produkte (mit den Unterseiten
 * Übersicht/Features/Roadmap je Produkt) · Priorisierung · Einstellungen.
 * Sie waechst mit den Meilensteinen mit — hier steht nur, was auch existiert.
 * Der in §12 vorgesehene Jira-Reiter fehlt deshalb, bis die Anbindung (§11) da ist.
 *
 * Der Guard ist BEDIENKOMFORT, keine Sicherheit: Er verhindert, dass jemand auf einer
 * Seite landet, deren Daten die API ihm ohnehin verweigert. Verbindlich prüft der
 * Server (`server/src/plugins/rbac.ts`).
 */

import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import type { Role } from '@pmpro/shared';
import { useAuthStore } from './stores/auth';

declare module 'vue-router' {
  interface RouteMeta {
    /** Standard ist „Anmeldung nötig"; nur die Login-Seite setzt hier `true`. */
    public?: boolean;
    /** Wenn gesetzt: nur diese Rollen dürfen die Route sehen. */
    roles?: Role[];
    /** Titel für den Browser-Tab. */
    title?: string;
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('./views/LoginView.vue'),
    meta: { public: true, title: 'Anmelden' }
  },
  {
    path: '/',
    name: 'dashboard',
    component: () => import('./views/DashboardView.vue'),
    meta: { title: 'Dashboard' }
  },
  {
    path: '/produkte',
    name: 'produkte',
    component: () => import('./views/ProductListView.vue'),
    meta: { title: 'Produkte' }
  },
  {
    // Die Reiter eines Produkts sind Kindrouten — so bleibt der Rahmen (Kopf,
    // Reiterleiste) beim Wechsel stehen und nur der Inhalt tauscht.
    path: '/produkte/:id',
    component: () => import('./views/ProductDetailView.vue'),
    props: true,
    children: [
      {
        path: '',
        name: 'produkt',
        component: () => import('./views/product/ProductOverviewTab.vue'),
        meta: { title: 'Produkt' }
      },
      {
        path: 'features',
        name: 'produkt-features',
        component: () => import('./views/product/ProductFeaturesTab.vue'),
        meta: { title: 'Features' }
      },
      {
        path: 'roadmap',
        name: 'produkt-roadmap',
        component: () => import('./views/product/ProductRoadmapTab.vue'),
        meta: { title: 'Roadmap' }
      }
    ]
  },
  {
    // Eigene Route statt Unterseite des Produkts: Ein Feature wird verlinkt und
    // weitergegeben, und aus der Priorisierung springt man produktübergreifend
    // hierher.
    path: '/features/:id',
    name: 'feature',
    component: () => import('./views/FeatureDetailView.vue'),
    props: true,
    meta: { title: 'Feature' }
  },
  {
    path: '/priorisierung',
    name: 'priorisierung',
    component: () => import('./views/PrioritizationView.vue'),
    meta: { title: 'Priorisierung' }
  },
  {
    // „Meine Aufgaben" — was mir zugewiesen wurde. Jede angemeldete Person, keine
    // Rollenschranke.
    path: '/aufgaben',
    name: 'aufgaben',
    component: () => import('./views/TasksView.vue'),
    meta: { title: 'Meine Aufgaben' }
  },
  {
    // Persönliche Einstellungen — erreichbar über das Avatar-Menü, nicht über die
    // Hauptnavigation. Jede angemeldete Person, keine Rollenschranke.
    path: '/konto',
    name: 'konto',
    component: () => import('./views/KontoView.vue'),
    meta: { title: 'Benutzereinstellungen' }
  },
  {
    path: '/benutzer',
    name: 'benutzer',
    component: () => import('./views/UsersView.vue'),
    meta: { title: 'Benutzer', roles: ['admin'] }
  },
  {
    path: '/einstellungen',
    name: 'einstellungen',
    component: () => import('./views/SettingsView.vue'),
    meta: { title: 'Einstellungen', roles: ['admin'] }
  },
  {
    // Interne, bewusst nicht verlinkte Seite: zeigt Tokens und Bausteine.
    // „Mach das wie im Styleguide" statt langer Beschreibungen.
    path: '/styleguide',
    name: 'styleguide',
    component: () => import('./views/StyleguideView.vue'),
    meta: { title: 'Styleguide' }
  },
  {
    path: '/:pfad(.*)*',
    name: 'nicht-gefunden',
    component: () => import('./views/NotFoundView.vue'),
    meta: { title: 'Seite nicht gefunden' }
  }
];

export const router = createRouter({
  // Basis aus Vite (`base`, per BASE_PATH beim Bauen): `/` oder z. B. `/docker/pmpro/`.
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior: () => ({ top: 0 })
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();

  // Beim ersten Aufruf einmal klaeren, ob das Sitzungs-Cookie noch gilt.
  if (!auth.ready) await auth.restore();

  if (to.meta.public) {
    // Wer angemeldet ist, hat auf der Login-Seite nichts verloren.
    return auth.isAuthenticated ? { name: 'dashboard' } : true;
  }

  if (!auth.isAuthenticated) {
    // Das Ziel merken, damit es nach dem Anmelden direkt weitergeht.
    return { name: 'login', query: { weiter: to.fullPath } };
  }

  if (to.meta.roles && !auth.hasRole(...to.meta.roles)) {
    return { name: 'dashboard' };
  }

  return true;
});

router.afterEach((to) => {
  const titel = to.meta.title;
  document.title = titel ? `${titel} · PMPro` : 'PMPro';
});
